import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { formatCarrierAccessNames } from "kontrolEdge/helpers/carrierAccess";
import { keListTagFilterHaystack } from "kontrolEdge/components/Shared/mapKeListResourceTags";
import {
  accessTypeLabel,
  carrierAccessStatusPresentation,
  formatParentConnection,
} from "./CarrierAccessData";
import type { CarrierAccessItem } from "./carrierAccess.types";

// Date parser: accept DD/MM/YYYY, YYYY-MM-DD and ISO (same rules as Ports).
function parseDateInput(raw: string): Date | null {
  const s = raw.trim();
  const m1 = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m1) {
    const [, dd, mm, yyyy] = m1;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const m2 = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (m2) {
    const [, yyyy, mm, dd] = m2;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function makeBaseCarrierAccessFieldSpecs(): FilterField<CarrierAccessItem>[] {
  return [
    {
      label: "Carrier Access ID",
      key: "carrieraccessid",
      accessor: (r) => r.serviceId,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Tag",
      key: "tag",
      accessor: (r) => keListTagFilterHaystack(r),
      type: "string",
      inFreeText: true,
    },
    // Filter on the labels users see, not the raw enum values.
    {
      label: "Type",
      key: "type",
      accessor: (r) => accessTypeLabel(r.accessType),
      type: "string",
      inFreeText: true,
    },
    {
      // Display names plus the raw codes, so "Telmex" and "TELMEX-LM" find
      // the same rows — same rule the Ports and Extended PoP haystacks follow.
      label: "Carriers",
      key: "carriers",
      accessor: (r) =>
        [
          formatCarrierAccessNames(r.carrierAccess),
          ...r.carrierAccess.map((c) => c.carrierCode),
        ]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      // Parent connection ids and names, so a Port id finds its access.
      label: "Parent Connection",
      key: "parentconnection",
      accessor: (r) =>
        r.endpoints
          .flatMap((e) => [formatParentConnection(e), e.name ?? ""])
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Status",
      key: "status",
      accessor: (r) => carrierAccessStatusPresentation(r.status).label,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Term",
      key: "term",
      accessor: (r) => r.term,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Renew Date",
      key: "renewdate",
      accessor: (r) => r.renewDateRaw,
      type: "date",
      inFreeText: true,
      parseRhs: parseDateInput,
    },
  ];
}

export function makeCarrierAccessFields(
  isViewingAllCustomers: boolean
): FilterField<CarrierAccessItem>[] {
  const base = makeBaseCarrierAccessFieldSpecs();
  if (isViewingAllCustomers) {
    base.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) =>
        [r.customer?.name ?? "", r.customer?.chargebeeCustomerId ?? ""]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    });
  }
  for (const f of base) f.key ||= labelId(f.label);
  return base;
}
